import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useUser } from '../contexts/UserContext';
import axios from 'axios';
import { Loader } from '@googlemaps/js-api-loader';
import { OverlappingMarkerSpiderfier } from 'ts-overlapping-marker-spiderfier';
import { getHeartbeatMaxAgeMs } from '../utils/heartbeatPolicy';
import { timeAgo } from '../shared/utility/timeAgo';

const SITE_RADIUS_METERS = 600;
const LABEL_RADIUS_METERS = 500;
const FALLBACK_SPREAD_METERS = 20;

const isValidCoordinate = (value, minimum, maximum) => {
    const number = Number(value);
    return Number.isFinite(number) && number >= minimum && number <= maximum;
};

const hasValidGps = (heartbeat) => {
    const gps = heartbeat?.gps;
    return (
        isValidCoordinate(gps?.lat, -90, 90) &&
        isValidCoordinate(gps?.lon, -180, 180)
    );
};

const getSourcePosition = (heartbeat, sitePosition) => {
    if (hasValidGps(heartbeat)) {
        const gps = heartbeat.gps;
        return { lat: Number(gps.lat), lng: Number(gps.lon) };
    }

    return sitePosition;
};

const getFallbackPosition = (sitePosition, index, count) => {
    if (count < 2) return sitePosition;

    const angle = (2 * Math.PI * index) / count - Math.PI / 2;
    const latitudeOffset = (FALLBACK_SPREAD_METERS * Math.sin(angle)) / 111320;
    const longitudeScale = 111320 * Math.max(Math.cos(sitePosition.lat * Math.PI / 180), 0.01);
    const longitudeOffset = (FALLBACK_SPREAD_METERS * Math.cos(angle)) / longitudeScale;

    return {
        lat: sitePosition.lat + latitudeOffset,
        lng: sitePosition.lng + longitudeOffset,
    };
};

const getStatusColor = (source) => {
    const timestamp = Date.parse(source.heartbeat?.timestamp);
    const maxAgeMs = getHeartbeatMaxAgeMs(source.type);
    const now = Date.now();
    if(Number.isFinite(timestamp)){
        if(timestamp <= now && now - timestamp <= maxAgeMs){
            return '#16a34a'; 
        }
        else{
            return '#dc2626'; 
        }
    }else{
        return '#000000';
    }
}

const getTypeColor = (source) => {
    switch(source.type){
        case "phonebox":
            return '#2560eb';
        case "sensor":
            return '#00d5ff';
        default:
            return '#ffffff';
    }
}

const escapeHtml = (value) => String(value)
    .replaceAll('&', '&amp;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;')
    .replaceAll('"', '&quot;')
    .replaceAll("'", '&#039;');

const createInfoContent = (site, source, usesGps) => {
    const heartbeatJson = JSON.stringify(source.heartbeat ?? null, null, 2);
    const heartbeatAge = timeAgo(source.heartbeat?.timestamp);

    return `
        <div style="color: #111827; min-width: 260px; max-width: 420px;">
            <strong>${escapeHtml(source.name || `Source ${source.id}`)}</strong>
            <div>Site: ${escapeHtml(site.name)}</div>
            <div>Updated: ${escapeHtml(heartbeatAge)}</div>
            <div>Position: ${usesGps ? 'source GPS' : 'site center'}</div>
            <pre style="margin: 8px 0 0; max-height: 240px; overflow: auto; white-space: pre-wrap;">${escapeHtml(heartbeatJson)}</pre>
        </div>`;
};

const MapComponent = () => {
    const { user } = useUser();
    const mapRef = useRef(null);
    const overlaysRef = useRef([]);
    const spiderfierRef = useRef(null);
    const [map, setMap] = useState(null);
    const [loading, setLoading] = useState(false);
    const [statusMessage, setStatusMessage] = useState('');

    useEffect(() => {
        if (!user.token || map || !mapRef.current) {
            return undefined;
        }

        let isCancelled = false;
        const loader = new Loader({
            apiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY,
            version: 'weekly',
        });

        loader.load().then(() => {
            if (isCancelled || !mapRef.current) return;

            setMap(new window.google.maps.Map(mapRef.current, {
                center: { lat: 0, lng: 0 },
                zoom: 2,
            }));
        }).catch(error => {
            console.error('Failed to load Google Maps:', error);
            setStatusMessage('Failed to load the map.');
        });

        return () => {
            isCancelled = true;
        };
    }, [user.token, map]);

    const clearOverlays = useCallback(() => {
        spiderfierRef.current?.forgetAllMarkers();
        overlaysRef.current.forEach(overlay => overlay.setMap(null));
        overlaysRef.current = [];
    }, []);

    const fetchAndDraw = useCallback(async () => {
        if (!map || !user.token) return;

        setLoading(true);
        setStatusMessage('');
        clearOverlays();

        try {
            const response = await axios.get(
                `${process.env.REACT_APP_BACKEND_URL}/sources/by-site`,
                { headers: { Authorization: `Bearer ${user.token}` } },
            );
            const sites = Array.isArray(response.data) ? response.data : [];
            const bounds = new window.google.maps.LatLngBounds();
            if (!spiderfierRef.current) {
                spiderfierRef.current = new OverlappingMarkerSpiderfier(map, {
                    markersWontMove: true,
                    markersWontHide: true,
                    basicFormatEvents: true,
                    keepSpiderfied: true,
                    nearbyDistance: 30,
                });
            }
            const spiderfier = spiderfierRef.current;
            let plottedSites = 0;

            sites.forEach(site => {
                if (
                    !isValidCoordinate(site.latitude, -90, 90) ||
                    !isValidCoordinate(site.longitude, -180, 180)
                ) return;

                const sitePosition = {
                    lat: Number(site.latitude),
                    lng: Number(site.longitude),
                };
                const siteCircle = new window.google.maps.Circle({
                    map,
                    center: sitePosition,
                    radius: SITE_RADIUS_METERS,
                    strokeColor: '#2563eb',
                    strokeOpacity: 1,
                    strokeWeight: 3,
                    fillColor: '#3b82f6',
                    fillOpacity: 0.3,
                    clickable: false,
                    zIndex: 1,
                });

                const labelPosition = {
                    lat: sitePosition.lat + LABEL_RADIUS_METERS / 111320,
                    lng: sitePosition.lng,
                };

                const siteLabel = new window.google.maps.Marker({
                    map,
                    position: labelPosition,
                    title: site.name,
                    clickable: false,
                    zIndex: 2,
                    icon: {
                        path: window.google.maps.SymbolPath.CIRCLE,
                        scale: 0,
                        labelOrigin: new window.google.maps.Point(0, -16),
                    },
                    label: {
                        text: String(site.name),
                        color: '#1e3a8a',
                        fontSize: '13px',
                        fontWeight: 'bold',
                    },
                });

                overlaysRef.current.push(siteCircle, siteLabel);
                const circleBounds = siteCircle.getBounds();
                if (circleBounds) bounds.union(circleBounds);
                else bounds.extend(sitePosition);
                plottedSites += 1;

                const sources = site.sources || [];
                const fallbackSources = sources.filter(source => !hasValidGps(source.heartbeat));
                const fallbackIndexes = new Map(
                    fallbackSources.map((source, index) => [source.id, index]),
                );

                sources.forEach(source => {
                    const usesGps = hasValidGps(source.heartbeat);
                    const position = usesGps
                        ? getSourcePosition(source.heartbeat, sitePosition)
                        : getFallbackPosition(
                            sitePosition,
                            fallbackIndexes.get(source.id),
                            fallbackSources.length,
                        );
                    const statusColor = getStatusColor(source);
                    const typeColor = getTypeColor(source);
                    const marker = new window.google.maps.Marker({
                        position,
                        title: source.name,
                        zIndex: 3,
                        optimized: false,
                        icon: {
                            path: window.google.maps.SymbolPath.CIRCLE,
                            scale: 10,
                            fillColor: typeColor,
                            fillOpacity: 1,
                            strokeColor: statusColor,
                            strokeOpacity: 1,
                            strokeWeight: 3,
                        },
                    });
                    const infoWindow = new window.google.maps.InfoWindow({
                        content: createInfoContent(site, source, usesGps),
                    });

                    spiderfier.addMarker(marker, () => {
                        infoWindow.open({ map, anchor: marker });
                    });
                    overlaysRef.current.push(marker);
                    bounds.extend(position);
                });
            });

            if (plottedSites === 0) {
                setStatusMessage('No accessible sites were found.');
                return;
            }

            map.fitBounds(bounds);
            if (plottedSites === 1) {
                window.google.maps.event.addListenerOnce(map, 'idle', () => {
                    if (map.getZoom() > 18) map.setZoom(18);
                });
            }
        } catch (error) {
            console.error('Failed to fetch map data:', error);
            setStatusMessage('Failed to fetch site and source data.');
        } finally {
            setLoading(false);
        }
    }, [clearOverlays, map, user.token]);

    useEffect(() => {
        fetchAndDraw();
    }, [fetchAndDraw]);

    useEffect(() => clearOverlays, [clearOverlays]);

    if (!user.token) return null;

    return (
        <div style={{ height: '100%', width: '100%', display: 'flex', flexDirection: 'column' }}>
            <div style={{ marginBottom: '10px' }}>
                <button onClick={fetchAndDraw} disabled={loading || !map}>
                    {loading ? 'Loading...' : 'Refresh'}
                </button>
                {statusMessage && (
                    <span style={{ marginLeft: '10px', color: '#b00020' }}>
                        {statusMessage}
                    </span>
                )}
            </div>
            <div ref={mapRef} style={{ flex: 1, minHeight: '400px', width: '100%' }} />
        </div>
    );
};

export default MapComponent;
